import { Fragment, useEffect, useState } from "react";
import { BRAND, useProductName } from "@/lib/brand";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { colors, semanticColors, layout, heights, typography, spacing, iconSize, iconStroke, motion, shadows, radius } from "@/styles/tokens";
import { useCanSeeReportsOrFalse, useIsAdminOrFalse, useSessionUidOrNull } from "@/auth/AuthContext";
import { getReadiness } from "@/services/api";
import { useNavGroups } from "@/lib/sidebarState";
import { activeGroupId, isDropdown, itemMatchesPath, visibleNav, type NavGroup, type NavItem } from "@/lib/navModel";

// A child's label sits on the column of its group's label.
const CHILD_INDENT = spacing[16] + iconSize.lg + spacing[10];

/** The Admin's count of what is blocking quotes. Null = not an Admin, or the
 *  read failed (no number is better than a wrong one). Re-read on navigation
 *  so it follows what the Admin just entered. */
function useReadinessBadge(): number | null {
  const isAdmin = useIsAdminOrFalse();
  const { pathname } = useLocation();
  const [badge, setBadge] = useState<number | null>(null);
  useEffect(() => {
    if (!isAdmin) { setBadge(null); return undefined; }
    let live = true;
    void getReadiness().then((r) => { if (live) setBadge(r.ok && r.report ? r.report.badge : null); });
    return () => { live = false; };
  }, [isAdmin, pathname]);
  return isAdmin ? badge : null;
}

/** The product mark and name. The mark is the symbol-only logo; the name is TEXT "CPQ Lite"
 *  (D81, new-branding 2026-09-30; the new SVG mark is wider than tall, so the token is its WIDTH). Its left edge is the nav icons' left edge, and the header is exactly
 *  the top bar's height, so the sidebar and the top bar read as one frame. */
function Brand({ collapsed }: { collapsed: boolean }) {
  const productName = useProductName();
  return (
    <div
      data-testid="app-brand"
      className="flex items-center"
      style={{
        height: heights.topBar, flexShrink: 0, boxSizing: "border-box",
        borderBottom: `1px solid ${semanticColors.border}`,
        padding: collapsed ? 0 : `0 ${spacing[16]}px`,
        justifyContent: collapsed ? "center" : "flex-start",
        gap: spacing[8],
      }}
    >
      <img src={BRAND.logo} alt={BRAND.markAlt} style={{ width: heights.sidebarLogo, height: "auto", display: "block", flexShrink: 0 }} />
      {!collapsed && (
        <span
          data-testid="app-wordmark"
          style={{
            fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold,
            color: semanticColors.textPrimary, letterSpacing: typography.letterSpacing.tight,
            whiteSpace: "nowrap",
          }}
        >
          {productName}
        </span>
      )}
    </div>
  );
}

/** Full: right-aligned and vertically centred (the row is a centring flex).
 *  Rail: a small count on the icon. */
function ReadinessBadge({ count, rail }: { count: number; rail: boolean }) {
  return (
    <span
      data-testid="readiness-badge"
      aria-label={`${count} blocking`}
      style={{
        marginLeft: rail ? 0 : "auto",
        position: rail ? "absolute" : "static",
        top: rail ? spacing[2] : undefined,
        left: rail ? `calc(50% + ${spacing[2]}px)` : undefined,
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        minWidth: rail ? spacing[14] : spacing[20], height: rail ? spacing[14] : spacing[16],
        padding: `0 ${spacing[4]}px`, boxSizing: "border-box",
        borderRadius: radius.full, flexShrink: 0,
        backgroundColor: semanticColors.warning, color: semanticColors.onAccent,
        fontSize: rail ? 10 : typography.fontSize.xs, fontWeight: typography.fontWeight.medium,
        lineHeight: 1, fontVariantNumeric: "tabular-nums",
      }}
    >
      {count}
    </span>
  );
}

/** One row of the menu, link or group header. */
function rowStyle(rail: boolean, active: boolean, topLevel: boolean, indent: number = spacing[16]): React.CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: rail ? "center" : "flex-start",
    gap: spacing[10],
    padding: rail ? 0 : `0 ${spacing[16]}px 0 ${indent}px`,
    position: "relative",
    height: heights.control,
    fontSize: typography.fontSize.base,
    color: active ? semanticColors.textPrimary : semanticColors.textSecondary,
    backgroundColor: active ? semanticColors.hoverBg : "transparent",
    fontWeight: active || topLevel ? typography.fontWeight.medium : typography.fontWeight.regular,
    transition: `background-color ${motion.fast}`,
  };
}

function ActiveBar() {
  return (
    <span
      aria-hidden
      style={{
        position: "absolute", left: 0, top: 0, bottom: 0, width: spacing[2],
        // ⚠️ accentInk, not the bright accent. This bar carries no text -- it IS
        // the signal, and it is aria-hidden, so colour is its only channel. The
        // bright accent is 1.7:1 against the sidebar's white surface; accentInk
        // is 4.6:1.
        backgroundColor: semanticColors.accentInk,
      }}
    />
  );
}

function RowLabel({ children }: { children: React.ReactNode }) {
  return (
    <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0 }}>
      {children}
    </span>
  );
}

/** Up/Down/Home/End walk every visible row; Right opens a group, Left closes it
 *  or steps from a child back to its group. Tab and Enter stay native. */
function onNavKeyDown(e: React.KeyboardEvent<HTMLElement>, setOpen: (groupId: string, open: boolean) => void) {
  const rows = [...e.currentTarget.querySelectorAll<HTMLElement>("[data-nav-row]")];
  const here = e.target instanceof HTMLElement ? e.target.closest<HTMLElement>("[data-nav-row]") : null;
  const at = here ? rows.indexOf(here) : -1;
  if (!here || at < 0) return;
  const { groupId, parentGroup } = here.dataset;
  const expanded = here.getAttribute("aria-expanded") === "true";
  let target: HTMLElement | undefined;
  switch (e.key) {
    case "ArrowDown": target = rows[(at + 1) % rows.length]; break;
    case "ArrowUp": target = rows[(at - 1 + rows.length) % rows.length]; break;
    case "Home": target = rows[0]; break;
    case "End": target = rows[rows.length - 1]; break;
    case "ArrowRight":
      if (!groupId) return;
      if (expanded) target = rows[at + 1]; else setOpen(groupId, true);
      break;
    case "ArrowLeft":
      if (groupId && expanded) setOpen(groupId, false);
      else if (parentGroup) target = rows.find((r) => r.dataset.groupId === parentGroup);
      else return;
      break;
    default:
      return;
  }
  e.preventDefault();
  target?.focus();
}

export function Sidebar({
  collapsed, onToggle, drawer = false, open = false, onClose,
}: {
  collapsed: boolean;
  onToggle: () => void;
  /** Phone layout: the sidebar is a slide-in menu, off screen until `open`. */
  drawer?: boolean;
  open?: boolean;
  onClose?: () => void;
}) {
  const canSeeReports = useCanSeeReportsOrFalse();
  const isAdmin = useIsAdminOrFalse();
  const uid = useSessionUidOrNull();
  const { pathname } = useLocation();
  const badge = useReadinessBadge();
  const groups = visibleNav({ reports: canSeeReports, admin: isAdmin });
  const activeGroup = activeGroupId(groups, pathname);
  const { isOpen, setOpen } = useNavGroups(uid, activeGroup, pathname);
  // In the slide-in menu the labels are always shown.
  const rail = collapsed && !drawer;

  // The rail's label tooltip: ONE fixed element, positioned from the hovered or
  // focused item. It cannot be a child of the nav (an absolutely positioned
  // child outside the rail is clipped by the nav's overflow), and a native
  // `title` shows on hover only, never on keyboard focus.
  const [tip, setTip] = useState<{ label: string; top: number; left: number } | null>(null);
  const showTip = (label: string, el: HTMLElement) => {
    if (!rail) return;
    const r = el.getBoundingClientRect();
    setTip({ label, top: r.top + r.height / 2, left: r.right + spacing[8] });
  };
  const hideTip = () => setTip(null);
  useEffect(() => { if (!rail) setTip(null); }, [rail]);

  // Escape closes the slide-in menu.
  useEffect(() => {
    if (!drawer || !open || !onClose) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [drawer, open, onClose]);

  const hasBadge = badge !== null && badge > 0;

  const renderLink = (item: NavItem, parent?: NavGroup) => {
    const { to, id, label, icon: Icon } = item;
    const child = !!parent && !rail;
    const active = itemMatchesPath(item, pathname);
    return (
      <NavLink
        key={to}
        to={to}
        data-testid={`sidebar-item-${id}`}
        data-nav-row=""
        data-parent-group={child ? parent.id : undefined}
        className="sidebar-link"
        aria-label={rail ? label : undefined}
        onClick={() => { hideTip(); onClose?.(); }}
        onMouseEnter={(e) => showTip(label, e.currentTarget)}
        onMouseLeave={hideTip}
        onFocus={(e) => showTip(label, e.currentTarget)}
        onBlur={hideTip}
        style={{ display: "block" }}
      >
        {({ isActive }) => (
          <div
            className="sidebar-nav-item"
            data-active={active}
            // A quote at /saved-quotes/:id is this page, and NavLink cannot know.
            aria-current={active && !isActive ? "page" : undefined}
            style={rowStyle(rail, active, !child, child ? CHILD_INDENT : spacing[16])}
          >
            {active && <ActiveBar />}
            {!child && <Icon size={iconSize.lg} strokeWidth={iconStroke.regular} className="shrink-0" />}
            {!rail && <RowLabel>{label}</RowLabel>}
            {to === "/readiness" && hasBadge && <ReadinessBadge count={badge} rail={rail} />}
          </div>
        )}
      </NavLink>
    );
  };

  const renderGroup = (group: NavGroup) => {
    const { id, label, icon: Icon, items } = group;
    const expanded = isOpen(group);
    const headerId = `sidebar-group-${id}`;
    const listId = `sidebar-group-list-${id}`;
    // Closed, the header stands in for its items: the highlight and the Admin's
    // count must not vanish with them.
    const highlighted = id === activeGroup && !expanded;
    const showBadge = !expanded && hasBadge && items.some((i) => i.to === "/readiness");
    return (
      <div key={id}>
        <button
          id={headerId}
          type="button"
          data-testid={headerId}
          data-nav-row=""
          data-group-id={id}
          className="sidebar-link"
          aria-expanded={expanded}
          aria-controls={expanded ? listId : undefined}
          onClick={() => setOpen(id, !expanded)}
          style={{
            display: "block", width: "100%", padding: 0, border: "none", background: "none",
            textAlign: "left", cursor: "pointer", font: "inherit",
          }}
        >
          <div className="sidebar-nav-item" data-active={highlighted} style={rowStyle(false, highlighted, true)}>
            {highlighted && <ActiveBar />}
            <Icon size={iconSize.lg} strokeWidth={iconStroke.regular} className="shrink-0" />
            <RowLabel>{label}</RowLabel>
            {showBadge && <ReadinessBadge count={badge} rail={false} />}
            <span
              aria-hidden
              style={{
                display: "inline-flex", flexShrink: 0, marginLeft: showBadge ? 0 : "auto",
                color: semanticColors.textMuted,
                transform: expanded ? "rotate(90deg)" : undefined,
                transition: `transform ${motion.fast}`,
              }}
            >
              <ChevronRight size={iconSize.md} strokeWidth={iconStroke.regular} />
            </span>
          </div>
        </button>
        {/* Unmounted, not hidden: a closed group holds no tab stops. */}
        {expanded && (
          <div
            id={listId}
            role="group"
            aria-labelledby={headerId}
            style={{ display: "flex", flexDirection: "column", gap: spacing[2], marginTop: spacing[2] }}
          >
            {items.map((item) => renderLink(item, group))}
          </div>
        )}
      </div>
    );
  };

  const width = rail ? layout.sidebarWidthCollapsed : layout.sidebarWidth;
  const hidden = drawer && !open;

  return (
    <>
      {drawer && open && (
        <div
          data-testid="sidebar-backdrop"
          onClick={onClose}
          style={{ position: "fixed", inset: 0, zIndex: 19, backgroundColor: semanticColors.overlay }}
        />
      )}
      <aside
        id="app-sidebar"
        data-testid="app-sidebar"
        data-mode={drawer ? "drawer" : rail ? "rail" : "full"}
        data-open={drawer ? (open ? "true" : "false") : undefined}
        aria-label="Main navigation"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          width,
          borderRight: `1px solid ${semanticColors.border}`,
          backgroundColor: semanticColors.surface,
          display: "flex",
          flexDirection: "column",
          transition: drawer ? `transform ${motion.layoutShift}` : `width ${motion.layoutShift}`,
          zIndex: 20,
          transform: hidden ? "translateX(-100%)" : undefined,
          // Closed, it is neither visible nor focusable (no hidden tab stops).
          visibility: hidden ? "hidden" : "visible",
          boxShadow: drawer && open ? shadows.modal : undefined,
        }}
      >
        <Brand collapsed={rail} />

        {/* overflowX is pinned to "hidden" deliberately, and it is not
            cosmetic: CSS computes a `visible` overflow on one axis to `auto`
            the moment the other axis is scrollable, so `overflowY: auto`
            alone silently made this nav a HORIZONTAL scroll container too.
            The vertical scroll (for short viewports) is kept. The rail's
            tooltip is a fixed element outside this nav for the same reason. */}
        <nav
          aria-label="Sections"
          onKeyDown={(e) => onNavKeyDown(e, setOpen)}
          style={{
            flex: 1, minHeight: 0, overflowY: "auto", overflowX: "hidden",
            display: "flex", flexDirection: "column", gap: spacing[4], paddingTop: spacing[12],
          }}
        >
          {/* The rail is flat: every page one click away, a rule between groups.
              A group header there would be an icon that goes nowhere. */}
          {groups.map((group, i) => {
            if (rail) {
              return (
                <Fragment key={group.id}>
                  {i > 0 && (
                    <div
                      aria-hidden
                      data-testid="sidebar-rail-divider"
                      style={{
                        flexShrink: 0, height: 1, margin: `${spacing[2]}px ${spacing[12]}px`,
                        backgroundColor: semanticColors.border,
                      }}
                    />
                  )}
                  {group.items.map((item) => renderLink(item, group))}
                </Fragment>
              );
            }
            return isDropdown(group) ? renderGroup(group) : renderLink(group.items[0]);
          })}
        </nav>

        {!drawer && (
          <button
            data-testid="sidebar-toggle"
            onClick={onToggle}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            aria-expanded={!collapsed}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            style={{
              flexShrink: 0,
              width: heights.iconButtonSm, height: heights.iconButtonSm, padding: 0,
              margin: `${spacing[8]}px auto`,
              cursor: "pointer", background: "transparent", border: "none", color: semanticColors.textSecondary,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            {collapsed
              ? <ChevronsRight size={iconSize.lg} strokeWidth={iconStroke.regular} />
              : <ChevronsLeft size={iconSize.lg} strokeWidth={iconStroke.regular} />}
          </button>
        )}
      </aside>

      {tip && (
        <div
          data-testid="sidebar-tooltip"
          role="tooltip"
          style={{
            position: "fixed", top: tip.top, left: tip.left, transform: "translateY(-50%)",
            zIndex: 40, pointerEvents: "none", whiteSpace: "nowrap",
            padding: `${spacing[4]}px ${spacing[8]}px`, borderRadius: radius.base,
            backgroundColor: semanticColors.darkSurfaceBg, color: colors.white,
            fontSize: typography.fontSize.sm, boxShadow: shadows.tooltip,
          }}
        >
          {tip.label}
        </div>
      )}
    </>
  );
}
